/**
 * Navbar sticky. Porte de `bicicletas_peniel_prototipo.html` linhas 48-87.
 *
 * O protótipo tinha vários problemas de acessibilidade no hambúrguer, todos
 * corrigidos aqui:
 *  - sem `aria-expanded`/`aria-controls` — leitor de tela não sabia o estado;
 *  - o ícone nunca virava X;
 *  - `focus:outline-none` sem substituto, então quem usa teclado ficava perdido;
 *  - nada de Escape, trava de scroll ou gestão de foco;
 *  - o painel ficava só `hidden` por classe, mas continuava na ordem de
 *    tabulação em algumas combinações;
 *  - o botão "Fale Conosco" do menu NÃO fechava o painel — o handler do
 *    protótipo só ligava em elementos `<a>`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MENSAGENS } from '@peniel/shared'

import { Icone } from '@/shared/ui/Icone.jsx'
import { useTravaScroll } from '@/shared/hooks/useTravaScroll.js'
import { useTeclaEscape } from '@/shared/hooks/useTeclaEscape.js'
import { BotaoWhatsAppGeral } from '../componentes/BotaoWhatsApp.jsx'
import { LinkAncora, type Ancora } from './LinkAncora.jsx'

const ITENS: Array<{ ancora: Ancora; rotulo: string }> = [
  { ancora: 'inicio', rotulo: 'Início' },
  { ancora: 'novas', rotulo: 'Novas' },
  { ancora: 'usadas', rotulo: 'Usadas' },
  { ancora: 'sobre', rotulo: 'Sobre Nós' },
]

export function Navbar() {
  const [aberto, definirAberto] = useState(false)
  const refGatilho = useRef<HTMLButtonElement>(null)
  const refPainel = useRef<HTMLDivElement>(null)
  const localizacao = useLocation()

  const fechar = useCallback(() => definirAberto(false), [])

  useTravaScroll(aberto)
  useTeclaEscape(aberto, fechar)

  // Trocar de rota com o menu aberto tem de fechá-lo.
  useEffect(() => {
    definirAberto(false)
  }, [localizacao.pathname])

  // Foco entra no painel ao abrir e VOLTA para o gatilho ao fechar — sem isso,
  // quem usa teclado é jogado para o começo da página.
  useEffect(() => {
    if (aberto) refPainel.current?.querySelector<HTMLElement>('a, button')?.focus()
    else if (document.activeElement === document.body) refGatilho.current?.focus()
  }, [aberto])

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-xs" aria-label="Principal">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <Icone nome="bike" className="text-3xl text-brand-600" />
              <span className="text-2xl font-bold tracking-tight text-gray-900">
                Bicicletas <span className="text-brand-600">Peniel</span>
              </span>
            </Link>
          </div>

          {/* ── desktop ── */}
          <div className="hidden items-center space-x-8 md:flex">
            {ITENS.map((item) => (
              <LinkAncora
                key={item.ancora}
                ancora={item.ancora}
                className="font-medium text-gray-600 transition hover:text-brand-600"
              >
                {item.rotulo}
              </LinkAncora>
            ))}
            <BotaoWhatsAppGeral mensagem={MENSAGENS.duvida} variante="primaria">
              Fale Conosco
            </BotaoWhatsAppGeral>
          </div>

          {/* ── gatilho mobile ── */}
          <div className="flex items-center md:hidden">
            <button
              ref={refGatilho}
              type="button"
              onClick={() => definirAberto((v) => !v)}
              aria-expanded={aberto}
              aria-controls="menu-mobile"
              aria-label={aberto ? 'Fechar menu' : 'Abrir menu'}
              className="inline-flex size-11 items-center justify-center rounded-lg text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
            >
              <Icone nome={aberto ? 'fechar' : 'menu'} className="text-2xl" />
            </button>
          </div>
        </div>
      </div>

      {/* ── painel mobile ──
          `hidden` de verdade (atributo, não classe) quando fechado: sai da
          árvore de acessibilidade e da ordem de tabulação. */}
      <div
        id="menu-mobile"
        ref={refPainel}
        hidden={!aberto}
        className="border-t border-gray-100 bg-white pb-4 md:hidden"
      >
        {ITENS.map((item) => (
          <LinkAncora
            key={item.ancora}
            ancora={item.ancora}
            aoNavegar={fechar}
            className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-600"
          >
            {item.rotulo}
          </LinkAncora>
        ))}

        <div className="px-4 pt-2">
          {/* `aoClicar` fecha o painel — era exatamente o que faltava no
              protótipo, cujo handler só ligava em elementos <a>. */}
          <BotaoWhatsAppGeral
            mensagem={MENSAGENS.duvida}
            variante="primaria"
            blocoCompleto
            aoClicar={fechar}
          >
            Fale Conosco
          </BotaoWhatsAppGeral>
        </div>
      </div>
    </nav>
  )
}
